import { inject, Injectable } from '@angular/core';
import { FeatureFlagService } from '../../../common/services/feature-flag.service';
import { OrderStepperStateService } from '../../../common/store/state/order/orderv2/workflows/order-stepper/order-stepper.state.service';
import { orderStepMetadata } from '../order-step-metadata';
import { OrderRoutes } from '../orders-routes.enum';
import { FlowContext } from './flow.types';
import { PRE_FLOW_STEPPER, STEPPER_FLOWS } from './stepper.flow';
import {
    StepperChildView, StepperRingSegment, StepperSectionDefinition, StepperSectionView, StepperStatus, StepperView
} from './stepper.types';

/** Ring of a 28px step circle with a 4px stroke (Figma stepper). */
export const STEPPER_RING_RADIUS = 12;
const RING_CIRCUMFERENCE = 2 * Math.PI * STEPPER_RING_RADIUS;
/** Leading completed sections collapse into one pill only from this many. */
const MIN_GROUPED_SECTIONS = 2;

const ORDER_ROUTE_PREFIX = '/order/';
const SUMMARY_SECTION_ID = 'summary';
/** Progress key for the pages before a product flow is picked. */
const PRE_FLOW_KEY = 'preFlow';

/**
 * Builds the order stepper for the active product flow and records progress: a page is visited when the
 * agent lands on it and completed when the agent moves forward from it. Jumping through the stepper never
 * completes a page. Behind the isOrderStepperEnabled feature flag: when it is off nothing is shown or recorded.
 */
@Injectable({ providedIn: 'root' })
export class OrderStepperService {
    private stepperState = inject(OrderStepperStateService);
    private featureFlags = inject(FeatureFlagService);

    /** Stepper for the current flow, or null when the page is not part of that flow's product steps. */
    build(ctx: FlowContext, url: string): StepperView | null {
        if (!this.featureFlags.isOrderStepperEnabled()) {
            return null;
        }
        const flow = this.flowKey(ctx);
        const sections = this.definition(ctx);
        const route = this.normalize(url);
        if (!sections.some(s => s.children.some(c => c.route === route))) {
            return null;
        }

        const progress = this.stepperState.flowProgress(flow);
        const views: StepperSectionView[] = [];
        for (const sectionDef of sections) {
            const children: StepperChildView[] = sectionDef.children
                .filter(child => child.route === route || progress.visited.includes(child.route)
                    || progress.completed.includes(child.route) || (child.when ? child.when(ctx) : true))
                .map(child => ({
                    route: child.route,
                    title: this.title(child.route),
                    current: child.route === route,
                    status: progress.completed.includes(child.route) ? StepperStatus.Completed
                        : (child.route === route || progress.visited.includes(child.route)) ? StepperStatus.InProgress
                        : StepperStatus.NotStarted
                }));
            if (!children.length) {
                continue;
            }
            const done = children.filter(c => c.status === StepperStatus.Completed).length;
            views.push({
                id: sectionDef.id,
                label: sectionDef.label,
                number: views.length + 1,
                current: children.some(c => c.current),
                done,
                total: children.length,
                children,
                ring: this.ring(children),
                status: done === children.length ? StepperStatus.Completed
                    : children.some(c => c.status !== StepperStatus.NotStarted) ? StepperStatus.InProgress
                    : StepperStatus.NotStarted
            });
        }

        let groupedCount = 0;
        while (groupedCount < views.length && views[groupedCount].status === StepperStatus.Completed
            && !views[groupedCount].current) {
            groupedCount++;
        }
        const completedCount = views.filter(v => v.status === StepperStatus.Completed).length;
        const summary = views[views.length - 1];

        return {
            sections: views,
            groupedCount: groupedCount >= MIN_GROUPED_SECTIONS ? groupedCount : 0,
            completedCount,
            readyToFinalize: summary?.id === SUMMARY_SECTION_ID && summary.current && views.slice(0, -1).every(v => v.status === StepperStatus.Completed),
            locked: this.stepperState.locked()
        };
    }

    /**
     * Called for every OrderBase navigation. Moving forward completes the page being left; reaching Payment
     * locks the stepper.
     */
    recordNavigation(ctx: FlowContext, fromUrl: string, toUrl: string): void {
        if (!this.featureFlags.isOrderStepperEnabled()) {
            return;
        }
        const to = this.normalize(toUrl);
        if (to === OrderRoutes.PAYMENT_INFORMATION) {
            this.stepperState.lock();
            return;
        }
        const flow = this.flowKey(ctx);
        const order = this.orderedRoutes(ctx);
        const fromIndex = order.indexOf(this.normalize(fromUrl));
        const toIndex = order.indexOf(to);
        if (fromIndex >= 0 && toIndex > fromIndex) {
            this.stepperState.markCompleted(flow, [order[fromIndex]]);
        }
    }

    /**
     * Called when a page of the flow is shown. The first time a flow is entered past its first step (LIC
     * launch, or a draft saved before the stepper existed), the steps shown before it are treated as done.
     */
    recordArrival(ctx: FlowContext, url: string): void {
        if (!this.featureFlags.isOrderStepperEnabled()) {
            return;
        }
        const flow = this.flowKey(ctx);
        const route = this.normalize(url);
        if (route === OrderRoutes.PAYMENT_INFORMATION) {
            this.stepperState.lock();
            return;
        }
        const progress = this.stepperState.flowProgress(flow);
        const sections = this.definition(ctx);
        if (!sections.some(s => s.children.some(c => c.route === route))) {
            return;
        }
        // Pre-flow pages are done once a product flow is entered, not while the agent is still on them.
        const doneOnEntry = !ctx.currentFlow() ? [] : sections
            .flatMap(s => s.children.filter(c => c.doneOnEntry).map(c => c.route))
            .filter(r => !progress.completed.includes(r));
        if (doneOnEntry.length) {
            this.stepperState.markCompleted(flow, doneOnEntry);
        }
        if (!progress.visited.length) {
            const earlier = this.earlierShownRoutes(ctx, route);
            if (earlier.length) {
                this.stepperState.markCompleted(flow, earlier);
            }
        }
        this.stepperState.markVisited(flow, [route]);
    }

    private earlierShownRoutes(ctx: FlowContext, route: string): string[] {
        const earlier: string[] = [];
        for (const sectionDef of this.definition(ctx)) {
            for (const child of sectionDef.children) {
                if (child.route === route) {
                    return earlier;
                }
                if (!child.when || child.when(ctx)) {
                    earlier.push(child.route);
                }
            }
        }
        return [];
    }

    private orderedRoutes(ctx: FlowContext): string[] {
        return this.definition(ctx).flatMap(s => s.children.map(c => c.route));
    }

    private flowKey(ctx: FlowContext): string {
        return ctx.currentFlow() ?? PRE_FLOW_KEY;
    }

    private definition(ctx: FlowContext): StepperSectionDefinition[] {
        const flow = ctx.currentFlow();
        const definition = flow ? STEPPER_FLOWS[flow] : PRE_FLOW_STEPPER;
        return definition ? definition(ctx) : [];
    }

    private ring(children: StepperChildView[]): StepperRingSegment[] {
        const segment = RING_CIRCUMFERENCE / children.length;
        return children.map((child, index) => ({
            dashArray: `${segment} ${RING_CIRCUMFERENCE - segment}`,
            dashOffset: -index * segment,
            completed: child.status === StepperStatus.Completed
        }));
    }

    private title(route: string): string {
        const path = route.startsWith(ORDER_ROUTE_PREFIX) ? route.substring(ORDER_ROUTE_PREFIX.length) : route;
        return orderStepMetadata.find(step => step.path === path)?.data?.title ?? path;
    }

    private normalize(url: string): string {
        return (url ?? '').split('?')[0].split('#')[0];
    }
}
